import { useState, useRef, useEffect } from 'react';

/**
 * The value as it stood `delay` ms ago, so an effect keyed on it runs once the
 * typing stops rather than on every keystroke.
 *
 * Returns a one-element array — the shape the call sites already read.
 *
 * @param {*} value - Value to follow.
 * @param {Number} delay - (Optional) Number. Milliseconds to wait after the last change.
 *
 */

export function useDebounce(value, delay = 600) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timeout);
  }, [value, delay]);

  return [debouncedValue];
}

/**
 * The pushed value, applied after the delay. Unlike `useDebounce` the caller
 * hands the value in rather than the hook following one it is given.
 *
 * @param {*} initialValue - Value held until the first push settles.
 * @param {Number} delay - Number. setTimeout's delay.
 *
 */

export function useDebounceState(initialValue, delay) {
  const [debouncedValue, setDebouncedValue] = useState(initialValue);
  const debounceTimeout = useRef(null);

  function debounceFunction(value) {
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
  }

  return [debouncedValue, debounceFunction];
}
